import { Trans, useLingui } from '@lingui/react';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { type DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { useMemo } from 'react';
import { ShareholderOverviewDocument } from './ShareholderOverview.graphql';
import { toNumber } from 'lodash-es';
import { type GqlIdentity } from '../../../graphql/base-types.graphql';

export interface Row {
  identity: (DisplayIdentity & Pick<GqlIdentity, 'address'>) | null | undefined;
  email: string | null | undefined;
  shareClass: string;
  numberOfShares: string;
  percent: string;
}

export const ShareholderOverviewTable = ({ rows }: { rows: Row[] }) => {
  const {
    i18n: { locale },
  } = useLingui();
  const showAvatar = useMemo(() => rows.length <= 50, [rows]);

  const sum = useMemo(() => {
    return rows.reduce((sum, r) => sum + toNumber(r.numberOfShares), 0);
  }, [rows]);

  return (
    <Table>
      <thead>
        <tr>
          <th>
            <Trans id="Shareholder" />
          </th>
          <th>
            <Trans id="Email" />
          </th>
          <th>
            <Trans id="Share Class" />
          </th>
          <th align="right">
            <Trans id="Number of Shares" />
          </th>
          <th align="right">
            <Trans id="Share %" />
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={`${row.identity?.id}${i}`}>
            <td>
              {row.identity && (
                <Identity
                  identity={row.identity}
                  showAvatar={showAvatar}
                  editable
                  updateRefetchQueries={[ShareholderOverviewDocument]}
                />
              )}
            </td>
            <td>{row.email}</td>
            <td>{row.shareClass}</td>
            <td align="right">
              <NumberFormat locale={locale} maximumFractionDigits={0}>
                {row.numberOfShares}
              </NumberFormat>
            </td>
            <td align="right">
              <NumberFormat locale={locale} variant="percentage">
                {row.percent}
              </NumberFormat>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} />
          <td align="right">
            <NumberFormat locale={locale} maximumFractionDigits={0}>
              {sum}
            </NumberFormat>
          </td>
          <td align="right">100 %</td>
        </tr>
      </tfoot>
    </Table>
  );
};
